import { useCallback, useRef, memo, useState, useEffect } from 'react';

import { cn } from '@/lib/utils';

import './comment-menu.css';
import { MessageSquareIcon, ArrowUpRightIcon, BotIcon } from 'lucide-react';

import { MarkdownEditor } from '../editor/markdown-editor';

import { Editor } from '@tiptap/react';
import { Transaction } from '@tiptap/pm/state';
import { ulid } from 'ulid';

import { useZero } from '@/lib/zero';
import { collaboratorsQuery } from '@/services/repo';
import { useUser } from '@/services/user';
import { pullQuery } from '@/features/pulls/services/pulls';

import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

export type CommentSelectionRange = {
  path: string;
  startSide: 'LEFT' | 'RIGHT' | null;
  endSide: 'LEFT' | 'RIGHT' | null;
  startLine: number;
  endLine: number;
};

export const FloatingCommentMenu = memo(function ({
  top,
  left,
  inputMode,
  onCommentButtonClick,
  onAskAIClick,
  onInputClose,
}: {
  top: number;
  left: number;
  inputMode: boolean;
  onCommentButtonClick: () => void;
  onAskAIClick?: (selectionData: { selectedText: string; selectionRange: CommentSelectionRange }) => void;
  onInputClose: () => void;
}) {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { data: pull } = useQuery(pullQuery(owner, repo, number));
  const { data: collaborators } = useQuery(collaboratorsQuery(owner, repo));

  const [value, setValue] = useState('');
  const editorRef = useRef<Editor | null>(null);
  const savedRangesRef = useRef<Range[]>([]);
  const selectedSplitSideRef = useRef<'LEFT' | 'RIGHT' | null>(null);

  const { user } = useUser();
  const z = useZero();

  const createReviewComment = async ({ body, commentRange }: { body: string; commentRange: CommentSelectionRange }) => {
    if (!pull || !commentRange) {
      console.error('Cannot create comment: missing pull data or selection range');
      return;
    }

    try {
      const { path, startSide, endSide, startLine, endLine } = commentRange;
      // TODO: get the proper side when in unified view
      // TODO: handle different start and end sides
      const side = startSide || endSide || 'RIGHT';

      await z.mutate.ReviewCommentsTable.insert({
        gid: ulid(),
        author: {
          login: user.login,
          id: user.id,
          avatarUrl: user.avatar_url,
        },
        authorLogin: user.login,
        commitId: pull.head.sha,
        path,
        side,
        startLine: startLine && startLine < endLine ? startLine : null,
        line: endLine,
        owner,
        repo,
        pullNumber: number,
        body,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error creating comment:', error);
    }
  };

  // --- Simulated selection highlight ---
  const highlightClass = 'mesa-simulated-selection';
  // Track created highlight spans for efficient removal
  const highlightedSpansRef = useRef<Set<HTMLElement>>(new Set());

  // Optimize these handlers with useCallback to prevent recreation on every render
  const addSimulatedHighlight = useCallback((ranges: Range[]) => {
    // Process each range
    ranges.forEach((range) => {
      // Only highlight inside the diff viewer and correct split side
      const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          if (!node.parentElement) return NodeFilter.FILTER_REJECT;
          // Only highlight if node is inside mesa-diff and .diff-code
          const codeCell = node.parentElement.closest('.diff-code');
          if (!codeCell || !node.parentElement.closest('.mesa-diff')) return NodeFilter.FILTER_REJECT;
          // If split view, only highlight the correct side
          if (selectedSplitSideRef.current) {
            // Find the .diff-line row and all .diff-code cells in it
            const row = codeCell.closest('.diff-line');
            if (!row) return NodeFilter.FILTER_REJECT;
            const codeCells = Array.from(row.querySelectorAll('.diff-code'));
            if (codeCells.length === 2) {
              const cellIdx = codeCells.indexOf(codeCell);
              if (
                (selectedSplitSideRef.current === 'LEFT' && cellIdx !== 0) ||
                (selectedSplitSideRef.current === 'RIGHT' && cellIdx !== 1)
              ) {
                return NodeFilter.FILTER_REJECT;
              }
            }
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      let nodeCount = 0;
      let highlightedCount = 0;
      let node: Text | null = walker.nextNode() as Text | null;

      while (node) {
        nodeCount++;
        // Check if this text node is at least partially in the range
        const nodeRange = document.createRange();
        nodeRange.selectNodeContents(node);
        const compareStart = range.compareBoundaryPoints(Range.END_TO_START, nodeRange);
        const compareEnd = range.compareBoundaryPoints(Range.START_TO_END, nodeRange);

        if (compareStart === -1 && compareEnd === 1) {
          // Node is at least partially selected
          // Wrap in a span if not already
          if (node.parentElement && !node.parentElement.classList.contains(highlightClass)) {
            highlightedCount++;
            const span = document.createElement('span');
            span.className = highlightClass;
            node.parentElement.replaceChild(span, node);
            span.appendChild(node);
            highlightedSpansRef.current.add(span);
          }
        }
        node = walker.nextNode() as Text | null;
      }
    });
  }, []);

  const removeSimulatedHighlight = useCallback(() => {
    highlightedSpansRef.current.forEach((el) => {
      if (el.parentNode) {
        while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
        el.parentNode.removeChild(el);
      }
    });
    highlightedSpansRef.current.clear();
  }, []);

  // Handle comment menu events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        // Submit
        handleSubmitComment();
      } else if (e.key === 'Escape') {
        // Cancel
        setValue('');
        onInputClose();
      }
    },
    [value, onInputClose]
  );

  const handleBlur = useCallback(
    ({ event }: { event: FocusEvent; editor: Editor; transaction: Transaction }) => {
      // Check if the blur event is because focus moved to our submit button
      // This prevents the blur from closing the menu when clicking the submit button
      const relatedTarget = event.relatedTarget as Element;
      if (relatedTarget && relatedTarget.classList.contains('menu-btn')) {
        // Don't close if focus moved to our button
        return;
      }
      setValue('');
      onInputClose();
    },
    [onInputClose]
  );

  const handleSubmitComment = useCallback(() => {
    if (value.trim()) {
      // Extract comment details from highlighted elements
      const selectionDetails = extractSelectionDetails();

      if (selectionDetails) {
        createReviewComment({ body: value, commentRange: selectionDetails });
      } else {
        console.error('Could not determine comment location. Comment not created.');
      }
    }
    setValue('');
    editorRef.current?.commands.clearContent();
    onInputClose();
  }, [value, onInputClose, createReviewComment]);

  // Control focus and highlights on input mode change
  useEffect(() => {
    if (inputMode && editorRef.current) {
      editorRef.current.commands.focus();
      // Restore the selection highlight visually
      if (savedRangesRef.current.length > 0) {
        // Remove any previous highlights
        removeSimulatedHighlight();
        addSimulatedHighlight(savedRangesRef.current);
      }
    } else if (!inputMode) {
      // Clean up highlight when input closes
      removeSimulatedHighlight();
      selectedSplitSideRef.current = null;
    }
    // Clean up on unmount
    return () => removeSimulatedHighlight();
  }, [inputMode, addSimulatedHighlight, removeSimulatedHighlight]);

  // Precompute stable class strings - updated for dual button layout
  const containerClassName = cn(
    'absolute z-10 transform -translate-x-1/2 -translate-y-full',
    'flex items-center shadow-lg border border-stone-200',
    'overflow-hidden transition-all duration-300 ease-out',
    'will-change-[width,background-color]',
    inputMode
      ? ['w-[340px] bg-white px-4 py-2 rounded-2xl']
      : ['w-20 bg-stone-900 hover:bg-stone-700 px-1 py-1 gap-1 rounded-full']
  );

  // Handler for comment button click
  const handleCommentButtonClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Save the current selection range
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        // Save all ranges
        savedRangesRef.current = [];
        for (let i = 0; i < sel.rangeCount; i++) {
          savedRangesRef.current.push(sel.getRangeAt(i).cloneRange());
        }
        // Detect split side (only for split view)
        const anchorNode = sel.anchorNode;
        let codeCell: HTMLElement | null = null;
        if (anchorNode) {
          if (anchorNode.nodeType === Node.ELEMENT_NODE) {
            codeCell = (anchorNode as HTMLElement).closest('.diff-code');
          } else if (anchorNode.parentElement) {
            codeCell = anchorNode.parentElement.closest('.diff-code');
          }
        }
        if (codeCell) {
          const row = codeCell.closest('.diff-line');
          if (row) {
            const codeCells = Array.from(row.querySelectorAll('.diff-code'));
            if (codeCells.length === 2) {
              const cellIdx = codeCells.indexOf(codeCell);
              selectedSplitSideRef.current = cellIdx === 0 ? 'LEFT' : 'RIGHT';
            } else {
              selectedSplitSideRef.current = null;
            }
          } else {
            selectedSplitSideRef.current = null;
          }
        } else {
          selectedSplitSideRef.current = null;
        }
      }
      onCommentButtonClick();
      e.stopPropagation();
    },
    [onCommentButtonClick]
  );

  // Handler for AI button click
  const handleAskAIClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onAskAIClick) return;

      // Save the current selection range
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        // Save all ranges
        savedRangesRef.current = [];
        for (let i = 0; i < sel.rangeCount; i++) {
          savedRangesRef.current.push(sel.getRangeAt(i).cloneRange());
        }

        // Get the selected text
        const selectedText = sel.toString();

        // Detect split side (only for split view)
        const anchorNode = sel.anchorNode;
        let codeCell: HTMLElement | null = null;
        if (anchorNode) {
          if (anchorNode.nodeType === Node.ELEMENT_NODE) {
            codeCell = (anchorNode as HTMLElement).closest('.diff-code');
          } else if (anchorNode.parentElement) {
            codeCell = anchorNode.parentElement.closest('.diff-code');
          }
        }
        if (codeCell) {
          const row = codeCell.closest('.diff-line');
          if (row) {
            const codeCells = Array.from(row.querySelectorAll('.diff-code'));
            if (codeCells.length === 2) {
              const cellIdx = codeCells.indexOf(codeCell);
              selectedSplitSideRef.current = cellIdx === 0 ? 'LEFT' : 'RIGHT';
            } else {
              selectedSplitSideRef.current = null;
            }
          } else {
            selectedSplitSideRef.current = null;
          }
        } else {
          selectedSplitSideRef.current = null;
        }

        // Temporarily add simulated highlight to extract details
        removeSimulatedHighlight(); // Clean up any existing highlights
        addSimulatedHighlight(savedRangesRef.current);

        // Extract selection details
        const selectionDetails = extractSelectionDetails();

        // Clean up the temporary highlight
        removeSimulatedHighlight();

        if (selectionDetails && selectedText.trim()) {
          onAskAIClick({
            selectedText: selectedText.trim(),
            selectionRange: selectionDetails,
          });
        }
      }
      e.stopPropagation();
    },
    [onAskAIClick, addSimulatedHighlight, removeSimulatedHighlight]
  );

  const handleStopPropagation = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  return (
    <div className={containerClassName} style={{ top: Math.max(30, top), left }} onMouseDown={handleStopPropagation}>
      {inputMode ? (
        <div
          className="flex items-center w-full opacity-0 animate-fade-slide-in"
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <div className="flex-1 min-w-0 mr-2">
            <div className="max-h-32 overflow-y-auto overflow-x-hidden">
              <MarkdownEditor
                ref={editorRef}
                value={value}
                onChange={setValue}
                onBlur={handleBlur}
                showStaticMenu={false}
                showBubbleMenu={false}
                mentionCollaborators={collaborators || []}
                owner={owner}
                repo={repo}
                editable
                autofocus
              />
            </div>
          </div>
          <button
            type="button"
            className={cn(
              'rounded-lg bg-stone-200 hover:bg-stone-300 p-1 transition-colors menu-btn flex-shrink-0',
              inputMode ? 'visible' : ''
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmitComment();
            }}
            tabIndex={-1}
            title="Submit comment"
          >
            <ArrowUpRightIcon className="size-5 text-stone-700" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center w-full gap-1">
          {/* Comment button */}
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full bg-stone-700 hover:bg-stone-600 transition-colors cursor-pointer"
            onClick={handleCommentButtonClick}
            title="Add comment"
          >
            <MessageSquareIcon className="size-4 text-white" />
          </div>

          {/* AI chat button */}
          {onAskAIClick && (
            <div
              className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-500 transition-colors cursor-pointer"
              onClick={handleAskAIClick}
              title="Ask AI about this"
            >
              <BotIcon className="size-4 text-white" />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// Extract line numbers and path from selected elements
function extractSelectionDetails(): CommentSelectionRange | null {
  // Get all elements with the simulated selection class
  const highlightedElements = document.querySelectorAll('.mesa-simulated-selection');

  if (!highlightedElements || highlightedElements.length === 0) {
    return null;
  }

  // Find all parent diff line elements
  const diffLines = new Set<HTMLElement>();
  highlightedElements.forEach((el) => {
    const diffLine = el.closest('.diff-line');
    if (diffLine) {
      diffLines.add(diffLine as HTMLElement);
    }
  });

  if (diffLines.size === 0) {
    return null;
  }

  // Convert to array and sort by position in the document
  const sortedDiffLines = Array.from(diffLines).sort((a, b) => {
    // Use the compareDocumentPosition method to determine the relative position
    const position = a.compareDocumentPosition(b);
    // If b comes after a in the document
    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });

  const firstLine = sortedDiffLines[0];
  const lastLine = sortedDiffLines[sortedDiffLines.length - 1];

  // Find the diff container to get the file path
  const diffContainer = firstLine.closest('[data-file-path]') || firstLine.closest('.mesa-diff');

  if (!diffContainer) {
    return null;
  }

  // Get file path from container attributes
  const filePath =
    diffContainer.getAttribute('data-file-path') ||
    diffContainer.getAttribute('data-path') ||
    diffContainer.getAttribute('filename');

  if (!filePath) {
    return null;
  }

  // Determine start and end sides and line numbers

  // Helper to get line numbers from a diff line element
  const getLineInfo = (lineEl: HTMLElement): { line: number; side: 'LEFT' | 'RIGHT' | null } | null => {
    // Check which highlighted spans are in this line
    const highlightedSpans = lineEl.querySelectorAll('.mesa-simulated-selection');

    if (!highlightedSpans || highlightedSpans.length === 0) {
      return null;
    }

    // Find which side the highlight is on (LEFT or RIGHT)
    let side: 'LEFT' | 'RIGHT' | null = null;

    // Check if this is a split view (has multiple diff-code cells)
    const codeCells = Array.from(lineEl.querySelectorAll('.diff-code'));

    if (codeCells.length > 1) {
      // This is a split view, determine which side
      // Check if the highlighted spans are in the first or second cell
      const leftCell = codeCells[0];
      const rightCell = codeCells[1];

      const leftHighlights = leftCell.querySelectorAll('.mesa-simulated-selection');
      const rightHighlights = rightCell.querySelectorAll('.mesa-simulated-selection');

      if (leftHighlights.length > 0) {
        side = 'LEFT';
      } else if (rightHighlights.length > 0) {
        side = 'RIGHT';
      }
    } else {
      // This is a unified view
      side = null;
    }

    // Get line numbers
    const oldLineNum = lineEl.getAttribute('data-old-line-number');
    const newLineNum = lineEl.getAttribute('data-new-line-number');

    // If side is specified, use the corresponding line number
    if (side === 'LEFT' && oldLineNum) {
      return { line: Number(oldLineNum), side };
    } else if (side === 'RIGHT' && newLineNum) {
      return { line: Number(newLineNum), side };
    } else if (side === null) {
      // Unified view, prefer new line number
      return {
        line: Number(newLineNum || oldLineNum || 0),
        side: newLineNum ? 'RIGHT' : oldLineNum ? 'LEFT' : null,
      };
    }

    return null;
  };

  let startInfo = getLineInfo(firstLine);
  let endInfo = getLineInfo(lastLine);

  if (!startInfo || !endInfo) {
    return null;
  }

  if (startInfo.line > endInfo.line) {
    // Swap the start and end info
    [startInfo, endInfo] = [endInfo, startInfo];
  }

  return {
    path: filePath,
    startSide: startInfo.side,
    endSide: endInfo.side,
    startLine: startInfo.line,
    endLine: endInfo.line,
  };
}
